/**
 * MapReferenceLayers: reference layers (district maps, legislative
 * districts, census geography, tribal areas) for any live map, plus the
 * Layers control that toggles them. Used by the community-of-interest map;
 * the plan map wires the same pieces itself because its hover tooltip also
 * reads place names.
 *
 * Visibility is shared with the plan map (stores/overlays.ts), so a layer
 * turned on in one place is on in the other.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type maplibregl from 'maplibre-gl';
import type { TenantConfig } from '../../lib/api';
import { tenantLayers } from '../../lib/layers';
import { fetchReferenceLayers } from '../../lib/plans';
import { createOverlayManager, type OverlayManager } from '../../map/overlays';
import { useOverlayStore } from '../../stores/overlays';
import { LayersControl, type ReferenceStatus } from './LayersControl';

export function MapReferenceLayers({
  map,
  tenant,
  beforeId,
  context,
}: {
  map: maplibregl.Map;
  tenant: TenantConfig;
  /** Draw reference layers under this map layer (e.g. a drawn shape). */
  beforeId?: string;
  /** Which map this is on, for the panel's note. */
  context?: 'plan' | 'coi';
}): JSX.Element {
  const layers = useMemo(() => tenantLayers(tenant), [tenant]);
  const managerRef = useRef<OverlayManager | null>(null);
  const referenceQuery = useQuery({
    queryKey: ['reference-layers', tenant.slug],
    queryFn: fetchReferenceLayers,
    retry: false,
    staleTime: 30 * 60 * 1000,
  });

  useEffect(() => {
    useOverlayStore.getState().initFromTenant(tenant.slug, layers);
  }, [tenant.slug, layers]);

  useEffect(() => {
    const manager = createOverlayManager(map, layers, { beforeId });
    managerRef.current = manager;
    for (const [id, visible] of Object.entries(useOverlayStore.getState().visibility)) {
      manager.setVisible(id, visible);
    }
    return () => {
      managerRef.current = null;
      try {
        manager.destroy();
      } catch {
        // The map was already removed with its layers.
      }
    };
  }, [map, layers, beforeId]);

  useEffect(() => {
    const manager = managerRef.current;
    if (!manager || !referenceQuery.data) return;
    for (const m of referenceQuery.data) manager.setReferenceData(m.slug, m.geojson);
  }, [referenceQuery.data, map, layers]);

  const referenceStatus = useCallback(
    (slug: string): ReferenceStatus => {
      if (referenceQuery.isError) return 'missing';
      if (!referenceQuery.data) return 'loading';
      return referenceQuery.data.some((m) => m.slug === slug) ? 'ready' : 'missing';
    },
    [referenceQuery.data, referenceQuery.isError],
  );

  const onToggle = useCallback((id: string, visible: boolean) => {
    managerRef.current?.setVisible(id, visible);
  }, []);

  return (
    <LayersControl layers={layers} referenceStatus={referenceStatus} onToggle={onToggle} context={context} />
  );
}
